import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import {
  identityProviderMapError,
  isIdentityProviderError,
} from "../../identity-provider-error";
import { Auth } from "../../isomorphic-auth/isomorphic-auth";
import { authenticationQueryKey } from "../../queries/authentication-query-keys";

import type {
  ConfirmResetPasswordProps,
  SignInOutcome,
  SignInProps,
  UpdatePasswordProps,
} from "./use-authentication.types";

export const useAuthentication = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const enterAdmin = () =>
    navigate(`/home${location.search}`, {
      state: { fromLogin: true },
    });

  // Resolves to "totp" when the password was right and Cognito wants the
  // authenticator-app code next (OMG-2777): finish with confirmTotpCode.
  const signIn = async ({
    email,
    password,
  }: SignInProps): Promise<SignInOutcome> => {
    try {
      const result = await Auth.signIn({
        username: email.toLowerCase(),
        password,
      });

      if (result.success) {
        if (
          result.data.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
        ) {
          return "totp";
        }
        enterAdmin();
        return "signed-in";
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const confirmTotpCode = async (code: string) => {
    try {
      const result = await Auth.confirmSignIn({ challengeResponse: code });

      if (result.success && result.data.isSignedIn) {
        enterAdmin();
        return;
      }

      throw result.success
        ? new Error(
            `Unexpected sign-in step ${result.data.nextStep?.signInStep}`,
          )
        : result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const result = await Auth.signOut();

      if (result.success) {
        queryClient.removeQueries({
          queryKey: authenticationQueryKey.authentication(),
        });
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const result = await Auth.resetPassword({
        username: email.toLowerCase(),
      });

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      console.log(error);
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const confirmResetPassword = async ({
    email,
    confirmationCode,
    newPassword,
  }: ConfirmResetPasswordProps) => {
    try {
      const result = await Auth.confirmResetPassword({
        username: email.toLowerCase(),
        confirmationCode,
        newPassword,
      });

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const updatePassword = async (props: UpdatePasswordProps) => {
    try {
      const result = await Auth.updatePassword(props);

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  return {
    signIn,
    confirmTotpCode,
    signOut,
    resetPassword,
    confirmResetPassword,
    updatePassword,
  };
};
